/**
 * The Open-to-chat rule, in one place.
 *
 * Three surfaces read this: the arc on a peer's avatar in Active, the Open tab
 * itself, and my own toggle. They must agree about what "open" means and about
 * how long is left, so the comparison and the formatting live here rather than
 * being written out three times.
 *
 * Everything is a comparison against a lease — a moment in the future — never a
 * flag plus a timer. The server decides the moment; the client only ever asks
 * whether it has passed. That is what makes a phone which slept through the
 * lapse read it correctly on wake, with nothing to catch up and nothing to
 * sweep.
 */

/**
 * The durations offered when turning it on, in minutes.
 *
 * Mirrors `OPEN_TO_CHAT_MINUTES` in `backend/api/src/modules/chat/dto/chat.dto.ts`,
 * which validates against exactly this set — an option added here and not there
 * is a 400, so keep them in step.
 *
 * There is no "until I turn it off". A signal that says "free right now" and
 * then runs for a week is a lie the person forgot they told, and the toggle
 * already makes turning it off one tap.
 */
export const OPEN_TO_CHAT_MINUTES = [30, 60, 120, 180] as const;

export type OpenToChatMinutes = (typeof OPEN_TO_CHAT_MINUTES)[number];

/**
 * Is this lease still running?
 *
 * `now` is injected so the callers that matter can be tested without a clock,
 * and so a list can pass ONE timestamp for a whole render pass rather than
 * letting each row read a slightly different `Date.now()`.
 */
export function isOpenNow(
  openUntil: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!openUntil) return false;
  const at = Date.parse(openUntil);
  // A malformed value reads as CLOSED, never as open. The failure direction
  // matters: this drives a visible "available now" claim about a person, and
  // showing one that is not true is worse than missing one that is.
  return Number.isFinite(at) && at > now;
}

/**
 * Minutes remaining on a lease, rounded UP, or 0 once it has passed.
 *
 * Rounded up so a lease with forty seconds left reads "1m" rather than "0m" —
 * zero on a live signal reads as broken. The caller turns this into words
 * through i18n; no formatting happens here, because the unit words are
 * translated and this module is not.
 */
export function openMinutesLeft(
  openUntil: string | null | undefined,
  now: number = Date.now(),
): number {
  if (!openUntil) return 0;
  const at = Date.parse(openUntil);
  if (!Number.isFinite(at) || at <= now) return 0;
  return Math.ceil((at - now) / 60_000);
}

/**
 * The next moment at which something on screen stops being true.
 *
 * The answer to "when must this list be re-judged", and the reason it exists is
 * a defect worth naming. The first version of the Open tab sampled the clock
 * only when data arrived — on focus, on pull-to-refresh, on load. That is fine
 * for a list of conversations, which do not change while you look at them, and
 * wrong for a list of LEASES, which do. A person who opened the tab and stayed
 * there kept seeing a contact who had gone; worse, their own toggle read
 * "I'm Open · for 30m" indefinitely, because the label is derived from the same
 * frozen clock, so the countdown did not merely expire late — it stopped.
 *
 * The obvious fix is a ticking timer, and it is the wrong one: a one-second
 * tick re-renders the whole inbox forever to remove a ring that changes once.
 * Every lease is a KNOWN future moment, so the schedule is known too. This
 * returns the earliest one still ahead, and the caller arms a single timeout
 * for exactly that instant. One wakeup per expiry, none in between, and the
 * change lands on the boundary rather than near it.
 *
 * Returns null when nothing on screen has an expiry left to wait for — in which
 * case the caller arms nothing at all, which is the common case for an inbox
 * with nobody open.
 */
export function nextExpiryAt(
  leases: readonly (string | null | undefined)[],
  now: number = Date.now(),
): number | null {
  let soonest: number | null = null;
  for (const lease of leases) {
    if (!lease) continue;
    const at = Date.parse(lease);
    // Unparseable and already-passed leases are both skipped: `isOpenNow`
    // already reads them as closed, so there is nothing for a timer to change.
    if (!Number.isFinite(at) || at <= now) continue;
    if (soonest === null || at < soonest) soonest = at;
  }
  return soonest;
}

/**
 * The next moment the DISPLAYED remaining time changes.
 *
 * Separate from `nextExpiryAt`, and the distinction is the bug it fixes.
 * Scheduling to the expiry alone is correct for the arc — which only ever
 * appears or disappears — and wrong for the label, which is supposed to count
 * down. "I'm Open · for 2h 15m" sat unchanged for two and a quarter hours and
 * then vanished. The signal was never false, but a timer that does not move is
 * read as a broken one.
 *
 * The label is minute-resolution (`openMinutesLeft` rounds up, and
 * `formatCompactDuration` prints whole minutes), so it changes exactly when
 * that ceiling steps down — at `until - k*60_000`, never in between. Returning
 * those moments means one wakeup per displayed minute instead of one per
 * second: sixty times fewer renders for output that is identical.
 *
 * Nothing here reaches the network. The lease is a timestamp the client already
 * holds and the server already knows; counting down against it is arithmetic,
 * not a poll. There is no read and no write behind any tick.
 */
export function nextCountdownTickAt(
  openUntil: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!openUntil) return null;
  const at = Date.parse(openUntil);
  if (!Number.isFinite(at) || at <= now) return null;
  const minutesShown = Math.ceil((at - now) / 60_000);
  // The instant the ceiling becomes `minutesShown - 1`. For a lease 2m15s away
  // the label reads "3m" and changes in 15s, not in 60.
  const next = at - (minutesShown - 1) * 60_000;
  // The final step lands exactly on the expiry, which `nextExpiryAt` already
  // owns; returning it here too would arm two timers for one moment.
  return next > now && next < at ? next : null;
}
